import React, { useState } from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { ConfirmationModal } from '../../ui/Modal';
import { colors } from '../../../styles/theme/colors';
import { CATALOG_CTA_GRADIENT, CATALOG_CTA_GRADIENT_HOVER } from '../../products/catalogGradients';
import type { ClientManagementRow } from '../../crm/client-management/types';

export interface DeleteClientResult {
  clientId: number;
  success: boolean;
}

export interface DeleteClientConfirmationModalProps {
  open: boolean;
  clients: ClientManagementRow[];
  onClose: () => void;
  onConfirm: () => Promise<DeleteClientResult[]>;
  onDeleteComplete?: (results: DeleteClientResult[]) => void;
}

const DeleteClientConfirmationModal: React.FC<DeleteClientConfirmationModalProps> = ({
  open,
  clients,
  onClose,
  onConfirm,
  onDeleteComplete,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);

  if (!clients || clients.length === 0) return null;

  const handleConfirm = async () => {
    setIsDeleting(true);
    try {
      const results = await onConfirm();
      onClose();
      onDeleteComplete?.(results);
    } finally {
      setIsDeleting(false);
    }
  };

  const customContent =
    clients.length === 1 ? (
      <Box
        sx={(theme) => ({
          p: 2.5,
          borderRadius: '8px',
          backgroundColor:
            theme.palette.mode === 'dark'
              ? alpha(theme.palette.common.white, 0.06)
              : theme.palette.grey[50],
          border: `1px solid ${theme.palette.divider}`,
          textAlign: 'left',
          mt: 2,
        })}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Cliente
        </Typography>
        <Typography variant="body1" color="text.primary" sx={{ fontWeight: 600, mb: 1.5 }}>
          {clients[0].customerName}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
          Correo:{' '}
          <Box component="strong" sx={{ color: 'text.primary' }}>
            {clients[0].customerEmail ?? '—'}
          </Box>
        </Typography>
      </Box>
    ) : (
      <Box
        sx={(theme) => ({
          mt: 2,
          borderRadius: '8px',
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
          backgroundColor: theme.palette.background.paper,
        })}
      >
        <Table size="small">
          <TableHead>
            <TableRow
              sx={(theme) => ({
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? alpha(theme.palette.common.white, 0.06)
                    : theme.palette.grey[50],
              })}
            >
              <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'text.primary' }}>
                Nombre
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'text.primary' }}>
                Correo
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clients.map((client) => (
              <TableRow key={client.id}>
                <TableCell
                  sx={{ fontSize: '0.875rem', color: 'text.primary', borderColor: 'divider' }}
                >
                  {client.customerName}
                </TableCell>
                <TableCell
                  sx={{ fontSize: '0.875rem', color: 'text.primary', borderColor: 'divider' }}
                >
                  {client.customerEmail ?? '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );

  const title =
    clients.length === 1
      ? `Eliminar cliente ${clients[0].customerName}`
      : `Eliminar ${clients.length} clientes`;
  const checkboxLabel =
    clients.length === 1
      ? '¿Desea eliminar este cliente?'
      : `¿Desea eliminar estos ${clients.length} clientes?`;

  return (
    <ConfirmationModal
      open={open}
      onClose={onClose}
      onConfirm={handleConfirm}
      title={title}
      message={
        clients.length === 1
          ? `¿Desea eliminar al cliente "${clients[0].customerName}"?`
          : `¿Desea eliminar estos ${clients.length} clientes?`
      }
      confirmText={isDeleting ? 'Eliminando...' : 'Eliminar'}
      cancelText="Cancelar"
      variant="default"
      confirmButtonGradient={CATALOG_CTA_GRADIENT}
      confirmButtonGradientHover={CATALOG_CTA_GRADIENT_HOVER}
      accentIconColor={colors.primary.main}
      icon={<DeleteIcon />}
      loading={isDeleting}
      requireCheckbox={true}
      checkboxLabel={checkboxLabel}
      customContent={customContent}
    />
  );
};

export default DeleteClientConfirmationModal;
